import { Box, IconButton, Stack, Tooltip, Typography } from "@mui/material";
import { GridColDef, useGridApiRef } from "@mui/x-data-grid";
import { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { QuickSearchToolbar } from "./QuickSearchToolbar";
import { AppDataGrid } from "../../components/datagrid/AppDataGrid";
import { ReportDataTableType, useReport } from "../../contexts/ReportContext";
import { ReportHistory } from "../../models/TGOReport";
import {
  REPORT_FORMAT_EXTENSION,
  ReportFormat,
  ReportService,
  downloadFromUrl,
} from "../../service/report.service";
import { useAccount } from "../../contexts/AccountContext";
import dayjs, { Dayjs } from "dayjs";
import i18n, { common, input, report } from "../../i18n";
import { tableStyle } from "../emission/_components/data-table/EmissionDataTableStyle";

interface Props {
  reportDataTableType: ReportDataTableType;
}

export const ReportDataTable = (props: Props) => {
  const { t } = useTranslation([common, input, report], {
    nsMode: "fallback",
  });

  const apiRef = useGridApiRef();

  const { tgoReport, setTgoReport, reportDataTableType } = useReport();

  const { user } = useAccount();

  const downloadReport = async (id: string, format: ReportFormat) => {
    let result;
    try {
      result = await ReportService.downloadReport(
        id,
        reportDataTableType === "summary" ? "summary" : "tgo",
        format,
      );
    } catch {
      // error alert already shown by service
      return;
    }
    if (result) {
      // v3 download returns filename = report name without an extension; name the
      // file after the report name + the requested format's extension.
      downloadFromUrl(
        result.downloadUrl,
        `${result.filename}.${REPORT_FORMAT_EXTENSION[format]}`,
      );

      const temp = [...tgoReport];

      const index = tgoReport.findIndex((e) => e._id === id);

      temp[index].downloadCount = temp[index].downloadCount + 1;

      temp[index].lastDownloadedBy.displayName = user?.displayName ?? "";

      setTgoReport([...temp]);
    }
  };

  const columns: GridColDef<ReportHistory>[] = [
    {
      field: "id",
      headerName: t("ID"),
      minWidth: 80,
      disableColumnMenu: true,
      valueFormatter: (value) =>
        reportDataTableType === "summary" ? `RES-${value}` : `RET-${value}`,
    },
    {
      field: "reportStartDate",
      headerName: t("Report-Period"),
      disableColumnMenu: true,
      flex: 1,
      valueFormatter: (value: any, row) => {
        try {
          const start: Dayjs = dayjs(value.substring(0, 10));

          const end: Dayjs = dayjs(row.reportEndDate.substring(0, 10));

          if (start.isSame(end, "month")) {
            return start.locale(i18n.language).format("MMM YYYY");
          } else {
            return `${start.locale(i18n.language).format("MMM YYYY")} -\n${end
              .locale(i18n.language)
              .format("MMM YYYY")}`;
          }
        } catch (error) {
          return "";
        }
      },
      renderCell: (params) => {
        return (
          <Typography variant="text1" sx={{ whiteSpace: "pre-line" }}>
            {params.formattedValue}
          </Typography>
        );
      },
      minWidth: 120,
    },
    {
      field: "reportName",
      headerName: t("Reporting-Name"),
      disableColumnMenu: true,
      flex: 1,
      minWidth: 160,
    },

    {
      field: "reportType",
      headerName: t("Reporting-Type"),
      disableColumnMenu: true,
      flex: 1,
      minWidth: 120,
    },
    {
      field: "downloadCount",
      headerName: t("Download-Count"),
      disableColumnMenu: true,
      type: "string",
      flex: 1,
      minWidth: 120,
    },
    {
      field: "lastDownloadedBy",
      headerName: t("Last-Downloaded-By"),
      disableColumnMenu: true,
      flex: 1,
      valueFormatter: (_value, row) => row.lastDownloadedBy?.displayName ?? "",
      minWidth: 120,
    },
    {
      field: "createdBy",
      headerName: t("Created-By"),
      disableColumnMenu: true,
      flex: 1,
      valueFormatter: (_value, row) => row.createdBy?.displayName ?? "",
      minWidth: 120,
    },
    {
      field: "",
      disableColumnMenu: true,
      sortable: false,
      width: 110,
      renderCell: (params) => {
        // v3 rows expose one or more available formats; offer a download per
        // format (excel first), falling back to excel for legacy rows.
        const formats = (
          params.row.availableFormats?.length
            ? params.row.availableFormats
            : ["excel"]
        )
          .filter((f): f is ReportFormat => f === "excel" || f === "word")
          .sort((a, b) => (a === "excel" ? -1 : b === "excel" ? 1 : 0));

        return (
          <Stack direction="row" gap="6px">
            {formats.map((format) => (
              <Tooltip key={format} title={format.toUpperCase()}>
                <IconButton
                  id={`${params.row._id}-${format}`}
                  type="button"
                  sx={{
                    backgroundColor:
                      format === "word" ? "var(--word-blue)" : "var(--green)",
                  }}
                  onClick={() => downloadReport(params.row._id, format)}
                >
                  <img src="/img/document-download.svg" alt="" />
                </IconButton>
              </Tooltip>
            ))}
          </Stack>
        );
      },
    },
  ];

  const { setReportDataTableType } = useReport();

  useEffect(() => {
    setReportDataTableType(props.reportDataTableType);
  }, []);

  return (
    <Box width={"100%"} height={"100%"} maxHeight={window.innerHeight - 53}>
      <AppDataGrid
        rows={tgoReport}
        columns={columns}
        rowHeight={60}
        apiRef={apiRef}
        slots={{ toolbar: QuickSearchToolbar }}
        localeText={{
          toolbarExport: t("Export"),
          toolbarQuickFilterPlaceholder: `${t("search")}...`,
        }}
        slotProps={{
          toolbar: {
            showQuickFilter: true,
            quickFilterProps: {
              variant: "outlined",
            },
          },
        }}
        initialState={{
          pagination: {
            paginationModel: { page: 0, pageSize: 50 },
          },
        }}
        pageSizeOptions={[25, 50, 100]}
        sx={tableStyle()}
      />
    </Box>
  );
};
